
import React, { useState, useEffect } from 'react';
import { SimulationConfig } from './types';
import { GeneralDisplaySection } from './GeneralDisplaySection';
import { ModeSelectorSection } from './ModeSelectorSection';
import { EscapeSettingsSection } from './EscapeSettingsSection';
import { PhysicsSettingsSection } from './PhysicsSettingsSection';
import { AdvancedPhysicsSection } from './AdvancedPhysicsSection';
import { VisualStylesSection } from './VisualStylesSection';
import { GlobalVisualsSection } from './GlobalVisualsSection';
import { EscapeAdvancedSection } from './EscapeAdvancedSection';
import { DrawerAdvancedSection } from './DrawerAdvancedSection';
import { SpawnerSettingsSection } from './SpawnerSettingsSection';
import { SphereDisplaySettings } from './SphereDisplaySettings';
import { TrailSettingsSection } from './TrailSettingsSection';
import { AudioWorkshopTab } from './AudioWorkshopTab';
import { AudioVolumeSection } from './AudioVolumeSection';
import { AudioPostProcessingSection } from './AudioPostProcessingSection';
import { CustomSoundUploadSection } from './CustomSoundUploadSection';
import { SongConverterSection } from './SongConverterSection';
import { MelodyLibrarySection } from './MelodyLibrarySection';
import { StorageManager } from './StorageManager';
import { CameraSettingsSection } from './CameraSettingsSection';
import { ImpactFXSection } from './ImpactFXSection';
import { RhythmicModeSection } from './RhythmicModeSection';
import { ShapeSettingsSection } from './ShapeSettingsSection';
import { LayerSortingSection } from './LayerSortingSection';
import { SettingsSearchContext } from './SettingsContext';

interface SettingsPanelProps {
  config: SimulationConfig;
  setConfig: React.Dispatch<React.SetStateAction<SimulationConfig>>;
  showUI: boolean;
  isPlaying: boolean;
  setIsPlaying: (p: boolean) => void;
  onReset: () => void;
  onSave: () => void;
  saveStatus: string;
  onFileUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  isAnalyzing: boolean;
  analysisProgress?: number;
  onBallSoundUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  isUploadingBallSound: boolean;
}

type SimSubTab = 'GAMEPLAY' | 'PHYSICS' | 'LOOKS' | 'VIEW';

export const SettingsPanel: React.FC<SettingsPanelProps> = ({
  config, setConfig, showUI, isPlaying, setIsPlaying, onReset, onSave, saveStatus, onFileUpload, isAnalyzing, analysisProgress = 0, onBallSoundUpload, isUploadingBallSound
}) => {
  const [activeTab, setActiveTab] = useState<'SIM' | 'AUDIO' | 'DATA'>('SIM');
  const [activeSimSubTab, setActiveSimSubTab] = useState<SimSubTab>('GAMEPLAY');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    (window as any).setConfig = setConfig;
  }, [setConfig]);

  const SimSubTabButton = ({ id, label, icon }: { id: SimSubTab, label: string, icon: string }) => (
      <button 
        onClick={() => setActiveSimSubTab(id)} 
        className={`flex-1 flex flex-col items-center justify-center py-2 rounded-xl transition-all duration-200 border ${
            activeSimSubTab === id 
            ? (config.darkMode ? 'bg-neutral-800 border-neutral-600 text-white' : 'bg-white border-neutral-300 text-neutral-900 shadow-sm') 
            : 'border-transparent opacity-60 hover:opacity-100 hover:bg-black/5'
        }`}
      >
          <i className={`fa-solid ${icon} text-[10px] mb-1`}></i>
          <span className="text-[8px] font-black uppercase tracking-wider">{label}</span>
      </button>
  );

  return (
    <SettingsSearchContext.Provider value={searchQuery}>
    <div className={`absolute top-0 right-0 h-full flex items-center p-6 z-20 pointer-events-none`}>
      <div className={`flex flex-col gap-2 items-center backdrop-blur-3xl p-6 rounded-[2.5rem] shadow-2xl border pointer-events-auto transition-all duration-500 max-h-[95vh] overflow-y-auto scrollbar-hide w-[400px] ${showUI ? 'translate-x-0 opacity-100' : 'translate-x-[130%] opacity-0'} ${config.darkMode ? 'bg-neutral-900/95 border-neutral-800 text-white' : 'bg-white/95 border-white/60 text-neutral-800'}`}>
        
        {/* Search Bar */}
        <div className="w-full mb-2">
            <div className={`flex items-center px-4 py-3 rounded-2xl border transition-all ${config.darkMode ? 'bg-black/20 border-white/10 focus-within:bg-black/40 focus-within:border-white/30' : 'bg-neutral-100 border-neutral-200 focus-within:bg-white focus-within:border-neutral-300'}`}>
                <i className="fa-solid fa-search opacity-40 text-xs mr-3"></i>
                <input 
                    type="text" 
                    placeholder="Search settings..." 
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="bg-transparent outline-none w-full text-xs font-bold placeholder-opacity-40"
                />
                {searchQuery && (
                    <button onClick={() => setSearchQuery('')} className="opacity-40 hover:opacity-100">
                        <i className="fa-solid fa-times"></i>
                    </button>
                )}
            </div>
        </div>

        {/* Main Tabs (Hidden if searching) */}
        {!searchQuery && (
            <div className={`grid grid-cols-3 gap-1 p-1 w-full rounded-2xl mb-2 ${config.darkMode ? 'bg-neutral-800' : 'bg-neutral-100'}`}>
                <button onClick={() => setActiveTab('SIM')} className={`py-3 rounded-xl text-[9px] font-black uppercase tracking-widest transition-all ${activeTab === 'SIM' ? (config.darkMode ? 'bg-white text-black' : 'bg-black text-white') : 'text-neutral-500'}`}>Sim</button>
                <button onClick={() => setActiveTab('AUDIO')} className={`py-3 rounded-xl text-[9px] font-black uppercase tracking-widest transition-all ${activeTab === 'AUDIO' ? (config.darkMode ? 'bg-white text-black' : 'bg-black text-white') : 'text-neutral-500'}`}>Audio</button>
                <button onClick={() => setActiveTab('DATA')} className={`py-3 rounded-xl text-[9px] font-black uppercase tracking-widest transition-all ${activeTab === 'DATA' ? (config.darkMode ? 'bg-white text-black' : 'bg-black text-white') : 'text-neutral-500'}`}>Saves</button>
            </div>
        )}

        {/* --- SIMULATION TAB --- */}
        {(searchQuery || activeTab === 'SIM') && (
          <div className="w-full flex flex-col gap-2">
            
            {/* Quick Actions (Pause/Reset/Save) - Always visible in SIM */}
            <div className="flex items-center gap-2 w-full mb-2">
                <button onClick={() => setIsPlaying(!isPlaying)} className={`h-12 flex-1 rounded-xl flex items-center justify-center shadow-lg transition-all active:scale-95 ${isPlaying ? (config.darkMode ? 'bg-neutral-700 text-white' : 'bg-white text-neutral-900 border border-neutral-200') : 'bg-emerald-500 text-white'}`}>
                    <i className={`fa-solid ${isPlaying ? 'fa-pause' : 'fa-play'} text-lg`}></i>
                </button>
                <button onClick={onReset} className={`h-12 w-12 rounded-xl shadow-sm border flex items-center justify-center hover:rotate-180 transition-all duration-500 active:scale-90 ${config.darkMode ? 'bg-neutral-800 border-neutral-700 text-white' : 'bg-white border-neutral-100 text-neutral-800'}`}>
                    <i className="fa-solid fa-rotate-right"></i>
                </button>
                <button onClick={onSave} className={`h-12 flex-1 rounded-xl font-black text-[9px] uppercase tracking-widest shadow-lg transition-all active:scale-95 flex items-center justify-center gap-2 ${saveStatus === 'saved' ? 'bg-emerald-600 text-white' : (config.darkMode ? 'bg-neutral-800 text-white border border-neutral-700' : 'bg-neutral-900 text-white')}`}>
                    <i className={`fa-solid ${saveStatus === 'saved' ? 'fa-check' : 'fa-floppy-disk'}`}></i>
                    {saveStatus === 'saved' ? 'Saved' : 'Quick Save'}
                </button>
            </div>

            {/* Sub-Tabs for SIM (Hidden if searching) */}
            {!searchQuery && (
                <div className={`flex w-full p-1 rounded-2xl gap-1 mb-2 ${config.darkMode ? 'bg-neutral-900 border border-white/5' : 'bg-neutral-50 border border-neutral-200'}`}>
                    <SimSubTabButton id="GAMEPLAY" label="Gameplay" icon="fa-gamepad" />
                    <SimSubTabButton id="PHYSICS" label="Physics" icon="fa-atom" />
                    <SimSubTabButton id="LOOKS" label="Looks" icon="fa-palette" />
                    <SimSubTabButton id="VIEW" label="View" icon="fa-eye" />
                </div>
            )}

            {/* 1. GAMEPLAY CONTENT */}
            {(searchQuery || activeSimSubTab === 'GAMEPLAY') && (
                <>
                    <ModeSelectorSection config={config} setConfig={setConfig} />
                    
                    {/* Specific Mode Settings - Context Aware */}
                    {config.mode === 'RHYTHMIC' && <RhythmicModeSection config={config} setConfig={setConfig} />}
                    {(config.mode === 'SPAWNER' || searchQuery) && <SpawnerSettingsSection config={config} setConfig={setConfig} />}
                    {(config.mode === 'ESCAPE' || searchQuery) && <EscapeSettingsSection config={config} setConfig={setConfig} />}
                    {(config.mode === 'ESCAPE' || searchQuery) && <EscapeAdvancedSection config={config} setConfig={setConfig} />}
                    {(config.mode === 'DRAWER' || searchQuery) && <DrawerAdvancedSection config={config} setConfig={setConfig} />}
                </>
            )}

            {/* 2. PHYSICS CONTENT */}
            {(searchQuery || activeSimSubTab === 'PHYSICS') && (
                <>
                    {(config.mode !== 'RHYTHMIC' || searchQuery) && <PhysicsSettingsSection config={config} setConfig={setConfig} />}
                    <AdvancedPhysicsSection config={config} setConfig={setConfig} />
                </>
            )}

            {/* 3. LOOKS CONTENT */}
            {(searchQuery || activeSimSubTab === 'LOOKS') && (
                <>
                    <VisualStylesSection config={config} setConfig={setConfig} />
                    <ShapeSettingsSection config={config} setConfig={setConfig} />
                    <SphereDisplaySettings config={config} setConfig={setConfig} />
                    <TrailSettingsSection config={config} setConfig={setConfig} />
                    <ImpactFXSection config={config} setConfig={setConfig} />
                    <LayerSortingSection config={config} setConfig={setConfig} />
                </>
            )}

            {/* 4. VIEW CONTENT */}
            {(searchQuery || activeSimSubTab === 'VIEW') && (
                <>
                    <GeneralDisplaySection config={config} setConfig={setConfig} />
                    <CameraSettingsSection config={config} setConfig={setConfig} />
                    <GlobalVisualsSection config={config} setConfig={setConfig} />
                </>
            )}
          </div>
        )}
        
        {/* --- AUDIO TAB --- */}
        {(activeTab === 'AUDIO' && !searchQuery) && (
          <div className="flex flex-col gap-4 w-full">
            <AudioVolumeSection config={config} setConfig={setConfig} />
            <AudioWorkshopTab config={config} setConfig={setConfig} />
            <AudioPostProcessingSection config={config} setConfig={setConfig} />
            <CustomSoundUploadSection config={config} onBallSoundUpload={onBallSoundUpload} isUploadingBallSound={isUploadingBallSound} />
            <SongConverterSection config={config} onFileUpload={onFileUpload} isAnalyzing={isAnalyzing} analysisProgress={analysisProgress || 0} />
            <MelodyLibrarySection config={config} setConfig={setConfig} />
          </div>
        )}
        
        {/* --- DATA TAB --- */}
        {(activeTab === 'DATA' && !searchQuery) && (
          <div className="flex flex-col gap-4 w-full">
            <StorageManager config={config} setConfig={setConfig} />
          </div>
        )}
      </div>
    </div>
    </SettingsSearchContext.Provider>
  );
};
